import React, { startTransition } from 'react';
import { useLocation } from 'react-router-dom';
import { useThemeMode } from '../../../../hooks/v0/useThemeMode';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import { activeTabAtom } from '../../../../atoms/v3/uiAtoms';
import { chatInputFocusedAtom } from '../../../../atoms/v3/chatInputAtoms';

import { navigateAtom } from '../../../../atoms/v3/navigationAtoms';
import { getPlatformInfo } from '../../../../utils/platformUtils';
import { trackNavigationEvents } from '../../../../lib/analyticsEnhanced';

interface TabItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  activeIcon: React.ReactNode;
}

interface IOSTabBarProps {
  tabs: TabItem[];
  onTabChange?: (tabId: string) => void;
}

/**
 * iOS 风格的底部标签栏
 */
export const IOSTabBar: React.FC<IOSTabBarProps> = ({ tabs, onTabChange }) => {
  const navigate = useSetAtom(navigateAtom);
  const location = useLocation();
  const { isDarkMode } = useThemeMode();
  const [currentActiveTab, setActiveTab] = useAtom(activeTabAtom);
  const chatInputFocused = useAtomValue(chatInputFocusedAtom);

  // Determine active tab based on the *start* of the path
  const activeTab = location.pathname.split('/')[2] || 'chat'; // Default to 'chat' or another suitable default

  // 判断是否应该隐藏 tab bar（聊天页面且输入框有焦点，且在移动端）
  const isChatPage = location.pathname.includes('/chat');
  const isTaskV2Page = location.pathname.includes('/taskv2');
  const isProjectV2Page = location.pathname.includes('/projectv2');
  const isMePage = location.pathname.includes('/me');
  const platformInfo = getPlatformInfo();
  const isMobile = platformInfo.isIOS || platformInfo.isAndroid;
  const shouldHideTabBar = isChatPage && chatInputFocused && isMobile;

  // 判断是否应该隐藏顶部边框（plan、project 和 chat 页面有自己的顶部栏）
  const shouldHideTopBorder = isChatPage || isTaskV2Page || isProjectV2Page;

  // Update activeTabAtom if the derived activeTab differs
  React.useEffect(() => {
    if (activeTab !== currentActiveTab) {
      // Wrap state update in transition
      startTransition(() => {
        setActiveTab(activeTab);
      });
    }
  }, [activeTab, currentActiveTab]);

  // 处理标签点击
  const handleTabClick = (tabId: string) => {
    const defaultPath = tabId === 'chat' ? '/v3/chat' : `/v3/${tabId}`;

    console.log(`targetPath: ${defaultPath}`)
    console.log(`location.pathname: ${location.pathname}`)
    if (location.pathname === defaultPath) {
      return;
    }

    // 记录标签切换事件
    trackNavigationEvents.tabSwitched(currentActiveTab || 'unknown', tabId, {
      from_path: location.pathname,
      to_path: defaultPath,
      layout_mode: 'mobile'
    });

    // Wrap BOTH navigation and state update in startTransition
    startTransition(() => {
      console.log(`navigate path: targetPath`, defaultPath)
        navigate({path: defaultPath, replace: true });
        setActiveTab(tabId);
    });

    if (onTabChange) {
      onTabChange(tabId);
    }
  };

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-50 font-ios transition-transform duration-300 ease-in-out ${
      shouldHideTabBar ? 'translate-y-full' : 'translate-y-0'
    }`}>
      {/* 背景 */}
      <div className={`absolute inset-0 bottom-[calc(-1*env(safe-area-inset-bottom))] bg-white/80 dark:bg-ios-dark-gray6/80 backdrop-blur-ios ${
        shouldHideTopBorder ? '' : 'border-t border-ios-gray5 dark:border-ios-dark-gray5'
      }`}></div>

      {/* 标签内容 */}
      <div className="relative flex justify-around items-center h-[calc(4rem+env(safe-area-inset-bottom))] px-2 pt-1 pb-[env(safe-area-inset-bottom)]">
        {tabs.map((tab) => {
          // Use the derived activeTab for styling
          const isActive = activeTab === tab.id;

          // 统一的标签样式
          return (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              data-testid={`${tab.id}-tab`}
              className={`flex flex-col items-center justify-center w-full h-full transition-colors rounded-ios-lg ${
                isActive ? '' : 'hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray5/50'
              }`}
            >
              <div className="w-6 h-6 mb-1">
                {isActive ? tab.activeIcon : tab.icon}
              </div>
              <span className={`text-ios-caption ${
                isActive
                  ? 'text-ios-blue dark:text-ios-dark-blue font-medium'
                  : 'text-ios-gray dark:text-ios-dark-gray'
              }`}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
